import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentType,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  MdAdd as MdAddRaw,
  MdLocalOffer as MdLocalOfferRaw,
  MdSearch as MdSearchRaw,
  MdInfo as MdInfoRaw,
  MdDownload as MdDownloadRaw,
  MdVisibility as MdVisibilityRaw,
  MdOpenInNew as MdOpenInNewRaw,
  MdEdit as MdEditRaw,
  MdMoreVert as MdMoreVertRaw,
} from 'react-icons/md';
import { canEditAdminOffer } from '../../utils/admin-offer-edit';
import {
  getOfferResponseLabel,
  isOfferAcceptedLike,
  offerResponseBadgeClass,
} from '../../utils/offer-response-status';
import {
  ClearFiltersButton,
  PageHeader,
  PageSkeleton,
} from '@euroasiann/shared/ui';
import { EUROASIANN_OFFER_LABEL } from '@euroasiann/shared';
import { authenticatedFetch } from '../../lib/api';
import { cn } from '../../lib/utils';
import { downloadAdminOfferPDF, viewAdminOfferPDF } from '../../utils/offer-pdf';
import { Pagination } from '../../components/shared/Pagination';

const DEFAULT_LIMIT = 10;

type IconComp = ComponentType<{ className?: string }>;
const MdAdd = MdAddRaw as IconComp;
const MdLocalOffer = MdLocalOfferRaw as IconComp;
const MdSearch = MdSearchRaw as IconComp;
const MdInfo = MdInfoRaw as IconComp;
const MdDownload = MdDownloadRaw as IconComp;
const MdVisibility = MdVisibilityRaw as IconComp;
const MdOpenInNew = MdOpenInNewRaw as IconComp;
const MdEdit = MdEditRaw as IconComp;
const MdMoreVert = MdMoreVertRaw as IconComp;

interface AdminOffer {
  _id: string;
  quotationNumber: string;
  title: string;
  description?: string;
  status: string;
  totalAmount: number;
  currency: string;
  createdAt: string;
  items?: Array<Record<string, unknown>>;
  /** Canonical response from API: pending | accepted | partially_accepted | rejected */
  responseStatus?: 'pending' | 'accepted' | 'partially_accepted' | 'rejected';
  responseLabel?: string;
  vendorName?: string;
  rfqId?: { _id: string; rfqNumber?: string; title?: string } | string;
  metadata?: {
    recipientEmail?: string;
    recipientName?: string;
    brand?: string;
    contactName?: string;
    contactEmail?: string;
    formData?: Record<string, unknown>;
    terms?: Record<string, string | number | undefined>;
    items?: Array<Record<string, unknown>>;
    selectedItemIndexes?: number[];
    selectionType?: 'all' | 'partial';
    responseStatus?: 'pending' | 'accepted' | 'partially_accepted' | 'rejected';
    shippingMode?: 'vendor' | 'self';
    courierService?: string;
    awbNumber?: string;
    rejectionReason?: string;
    acceptedAt?: string;
    rejectedAt?: string;
    purchaseOrderId?: string;
    purchaseOrderNumber?: string;
    adminShippingRequested?: boolean;
    guestAccepted?: boolean;
  };
  linkedPurchaseOrder?: {
    _id: string;
    poNumber?: string;
    status: string;
    shippingMode?: 'vendor' | 'self' | null;
    shippingChargesConfigured?: boolean;
    adminShippingRequested?: boolean;
    proformaInvoiceSent?: boolean;
    proformaInvoiceApproved?: boolean;
    finalInvoiceIssued?: boolean;
    paymentUnlocked?: boolean;
  };
  vendorOrganizationId?: { _id?: string; name?: string } | string;
}

function shippingLabel(offer: AdminOffer): string {
  if (!isOfferAcceptedLike(offer)) return '—';
  const meta = offer.metadata || {};
  if (meta.shippingMode === 'vendor') return 'Vendor shipping';
  if (meta.shippingMode === 'self') {
    const bits = [meta.courierService, meta.awbNumber ? `AWB ${meta.awbNumber}` : ''].filter(Boolean);
    return bits.length ? `Self · ${bits.join(' · ')}` : 'Self shipment';
  }
  return '—';
}

function purchasesWorkflowLabel(offer: AdminOffer): string {
  const po = offer.linkedPurchaseOrder;
  const submitted = offer.status === 'submitted' || offer.status === 'pending';
  if (submitted && !po) return 'Vendor offer received — open RFQ to finalize';
  if (!po && (offer.status === 'finalized' || submitted)) {
    return 'Awarded — generating purchase order';
  }
  if (!po) return '—';
  const st = String(po.status || '').toLowerCase();
  if (st === 'issued' || st === 'draft') return 'PO issued — awaiting vendor acknowledgement';
  if (po.paymentUnlocked || po.finalInvoiceIssued) return 'Final invoice — payment unlocked';
  if (po.proformaInvoiceApproved) {
    if (po.shippingMode === 'self') return 'Self shipment confirmed — track AWB';
    if (!po.shippingMode) return 'Proforma accepted — choose vendor or self shipment';
    return 'Vendor shipping in progress';
  }
  if (po.proformaInvoiceSent) return 'Proforma received — review and accept';
  return 'Awaiting vendor proforma, then shipping and payment';
}

function workflowStageLabel(offer: AdminOffer): string {
  const meta = offer.metadata || {};
  const po = offer.linkedPurchaseOrder;
  const accepted = isOfferAcceptedLike(offer);

  if (!accepted) return '—';
  if (!po && meta.guestAccepted) return 'Accepted — assign customer org for PO';
  if (!po) return 'Generating PO & Documenso signature…';

  if (po.paymentUnlocked || po.finalInvoiceIssued) {
    return 'Final invoice issued — payment unlocked';
  }
  if (po.proformaInvoiceApproved) {
    if (po.shippingMode === 'self') return 'Proforma approved — self shipment confirmed';
    if (
      po.adminShippingRequested === true ||
      meta.adminShippingRequested === true
    ) {
      if (!po.shippingChargesConfigured) return 'Customer asked for shipping charges';
      return 'Shipping charges set — issue final invoice';
    }
    if (!po.shippingMode) return 'Proforma approved — await customer shipping choice';
    if (po.shippingMode === 'vendor' && !po.shippingChargesConfigured) {
      return 'Configure shipping charges, then issue final invoice';
    }
    return 'Issue final invoice (includes shipping if any)';
  }
  if (po.proformaInvoiceSent) {
    return 'Proforma sent — awaiting customer approval';
  }
  return 'Send proforma invoice (item prices only)';
}

function responseNotes(offer: AdminOffer, pipeline: 'sales' | 'purchases' = 'sales'): string {
  const meta = offer.metadata || {};
  if (meta.rejectionReason) return meta.rejectionReason;
  return pipeline === 'purchases' ? purchasesWorkflowLabel(offer) : workflowStageLabel(offer);
}

function offerCompany(offer: AdminOffer): string {
  return String(
    offer.metadata?.recipientName ||
      offer.metadata?.formData?.recipientName ||
      offer.metadata?.formData?.companyName ||
      ''
  ).trim();
}

function offerVendorName(offer: AdminOffer): string {
  if (offer.vendorName?.trim()) return offer.vendorName.trim();
  const vendor = offer.vendorOrganizationId;
  if (vendor && typeof vendor === 'object' && vendor.name) return String(vendor.name).trim();
  return offerCompany(offer);
}

function formatStatusLabel(status: string): string {
  return String(status || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatOfferDate(value?: string): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function statusBadgeClass(status: string): string {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'finalized' || normalized === 'ordered' || normalized === 'awarded') {
    return 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:ring-emerald-800';
  }
  if (normalized === 'rejected' || normalized === 'lost') {
    return 'bg-rose-50 text-rose-800 ring-1 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-200 dark:ring-rose-800';
  }
  return 'bg-slate-50 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900/40 dark:text-slate-200 dark:ring-slate-700';
}

function canOpenOffer(offer: AdminOffer): boolean {
  return (
    isOfferAcceptedLike(offer) ||
    getOfferResponseLabel(offer) === 'Rejected' ||
    offer.status === 'finalized' ||
    offer.status === 'submitted' ||
    offer.status === 'pending'
  );
}

function OfferRowActions({
  offer,
  isPurchases,
  onOpen,
  onEdit,
}: {
  offer: AdminOffer;
  isPurchases: boolean;
  onOpen: () => void;
  onEdit: () => void;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});

  const showEdit = !isPurchases && canEditAdminOffer(offer);
  const showOpen = canOpenOffer(offer);

  const updateMenuPosition = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const width = 168;
    let left = rect.right - width;
    if (left < 8) left = 8;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < 160;
    setMenuStyle({
      position: 'fixed',
      zIndex: 2300,
      top: openUp ? undefined : rect.bottom + 4,
      bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
      left,
      width,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const onMove = () => updateMenuPosition();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (buttonRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const menuItemClass =
    'flex w-full items-center gap-2.5 px-3 py-2 text-sm text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] transition-colors';

  return (
    <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
      <button
        ref={buttonRef}
        type="button"
        aria-label="Offer actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="p-2 rounded-md text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] transition-colors"
      >
        <MdMoreVert className="w-5 h-5" />
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={menuStyle}
              className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] py-1 shadow-lg"
            >
              {showOpen && (
                <button
                  type="button"
                  role="menuitem"
                  className={menuItemClass}
                  onClick={() => {
                    setOpen(false);
                    onOpen();
                  }}
                >
                  <MdOpenInNew className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                  Open
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                className={menuItemClass}
                onClick={() => {
                  setOpen(false);
                  void viewAdminOfferPDF(offer);
                }}
              >
                <MdVisibility className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                View
              </button>
              <button
                type="button"
                role="menuitem"
                className={menuItemClass}
                onClick={() => {
                  setOpen(false);
                  void downloadAdminOfferPDF(offer);
                }}
              >
                <MdDownload className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                Download
              </button>
              {showEdit && (
                <button
                  type="button"
                  role="menuitem"
                  className={menuItemClass}
                  onClick={() => {
                    setOpen(false);
                    onEdit();
                  }}
                >
                  <MdEdit className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
                  Edit
                </button>
              )}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export function OffersPage({
  embedded = false,
  pipeline = 'sales',
}: {
  embedded?: boolean;
  /** sales = admin offers to customers; purchases = vendor quotations received */
  pipeline?: 'sales' | 'purchases';
}) {
  const navigate = useNavigate();
  const isPurchases = pipeline === 'purchases';
  const salesOffersBase = '/dashboard/admin/sales?salesTab=offers';

  const goToNewOffer = () => {
    if (embedded && !isPurchases) {
      navigate(`${salesOffersBase}&offerView=new`);
      return;
    }
    navigate('/dashboard/admin/offers/new');
  };

  const goToOfferDetail = (offerId: string) => {
    if (embedded && !isPurchases) {
      navigate(`${salesOffersBase}&offerView=detail&offerId=${offerId}`);
      return;
    }
    navigate(`/dashboard/admin/offers/${offerId}`);
  };

  const goToOfferEdit = (offerId: string) => {
    if (embedded && !isPurchases) {
      navigate(`${salesOffersBase}&offerView=edit&offerId=${offerId}`);
      return;
    }
    navigate(`/dashboard/admin/offers/${offerId}/edit`);
  };

  const [filters, setFilters] = useState({
    title: '',
    vendor: '',
    rfq: '',
    response: '',
    status: '',
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [page, setPage] = useState(1);
  const [limit] = useState(DEFAULT_LIMIT);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setAppliedFilters(filters);
      setPage(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [filters]);

  type OffersQueryResult = {
    offers: AdminOffer[];
    total: number;
    totalPages: number;
    page: number;
    limit: number;
  };

  const { data: offersResult, isLoading, isFetching, error } = useQuery<OffersQueryResult>({
    queryKey: ['admin-offers', pipeline, appliedFilters, page, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('isAdminOffer', isPurchases ? 'false' : 'true');
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (appliedFilters.title.trim()) params.set('title', appliedFilters.title.trim());
      if (appliedFilters.vendor.trim()) params.set('vendor', appliedFilters.vendor.trim());
      if (appliedFilters.rfq.trim()) params.set('rfq', appliedFilters.rfq.trim());
      if (appliedFilters.response.trim()) params.set('response', appliedFilters.response.trim());
      if (appliedFilters.status.trim()) params.set('status', appliedFilters.status.trim());

      const response = await authenticatedFetch(`/api/v1/admin/quotations-all?${params.toString()}`);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to fetch offers');
      }
      const json = await response.json();
      const raw = Array.isArray(json.data)
        ? json.data
        : Array.isArray(json.data?.data)
          ? json.data.data
          : [];
      const pagination = json.pagination || json.data?.pagination || {};
      const total = Number(pagination.total ?? raw.length) || 0;
      const currentLimit = Number(pagination.limit ?? limit) || limit;
      const totalPages =
        Number(pagination.totalPages ?? Math.max(1, Math.ceil(total / currentLimit))) || 1;
      const currentPage = Number(pagination.page ?? page) || page;

      return {
        offers: raw as AdminOffer[],
        total,
        totalPages,
        page: currentPage,
        limit: currentLimit,
      };
    },
    refetchOnWindowFocus: false,
    staleTime: 15_000,
    placeholderData: (previous) => previous,
  });

  const offers = offersResult?.offers ?? [];
  const totalItems = offersResult?.total ?? 0;
  const totalPages = Math.max(1, offersResult?.totalPages ?? 1);

  const hasActiveFilters = Object.values(filters).some((value) => value.trim() !== '');

  const clearFilters = () => {
    const empty = {
      title: '',
      vendor: '',
      rfq: '',
      response: '',
      status: '',
    };
    setFilters(empty);
    setAppliedFilters(empty);
    setPage(1);
  };

  if (isLoading && offers.length === 0) {
    return <PageSkeleton variant="table" />;
  }

  if (error) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-800 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200">
        <div className="flex items-start gap-3">
          <MdInfo className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <h2 className="text-sm font-semibold">Unable to load offers</h2>
            <p className="mt-1 text-sm opacity-90">{(error as Error).message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'w-full bg-[hsl(var(--background))] text-[hsl(var(--foreground))]',
        embedded ? '' : 'min-h-screen p-4 lg:p-8'
      )}
    >
      {!embedded && (
        <PageHeader
          title={isPurchases ? 'Vendor Offers' : 'Offers'}
          subtitle={
            isPurchases
              ? 'Review quotations received from vendors against purchase RFQs.'
              : `Create and send ${EUROASIANN_OFFER_LABEL}s to customers by email.`
          }
          subtitleVariant="description"
          subtitleAccent={false}
          breadcrumb={[
            { label: 'Admin' },
            { label: isPurchases ? 'Purchases' : 'Offers', current: true },
          ]}
          actions={
            !isPurchases ? (
              <button
                type="button"
                onClick={goToNewOffer}
                className="inline-flex items-center gap-2 rounded-lg bg-[#0088cc] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#0077b3]"
              >
                <MdAdd className="h-5 w-5" />
                Send new offer
              </button>
            ) : undefined
          }
        />
      )}

      {embedded && (
        <div className="mb-5 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-base font-semibold tracking-tight text-[hsl(var(--foreground))]">
              {isPurchases ? 'Vendor offers' : 'Customer offers'}
            </h2>
            <p className="mt-0.5 text-sm text-[hsl(var(--muted-foreground))]">
              {isPurchases
                ? 'Quotations received from vendors. Finalize an offer from the linked RFQ to issue a purchase order.'
                : 'Offers sent to customers for RFQs received in Sales.'}
            </p>
          </div>
          {!isPurchases && (
            <button
              type="button"
              onClick={goToNewOffer}
              className="inline-flex items-center gap-2 rounded-lg bg-[#0088cc] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#0077b3]"
            >
              <MdAdd className="h-5 w-5" />
              Send new offer
            </button>
          )}
        </div>
      )}

      <div
        className={cn(
          'rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 shadow-sm',
          embedded ? 'mt-0' : 'mt-4'
        )}
      >
        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {(
            [
              ['title', 'Title', filters.title] as const,
              ['vendor', isPurchases ? 'Vendor' : 'Recipient', filters.vendor] as const,
              ['rfq', 'RFQ', filters.rfq] as const,
            ]
          ).map(([key, placeholder, value]) => (
            <label key={key} className="relative block">
              <span className="sr-only">{placeholder}</span>
              <MdSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
              <input
                type="text"
                placeholder={placeholder}
                value={value}
                onChange={(e) => setFilters((prev) => ({ ...prev, [key]: e.target.value }))}
                className="h-10 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] pl-9 pr-3 text-sm text-[hsl(var(--foreground))] outline-none transition-colors placeholder:text-[hsl(var(--muted-foreground))] focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/15"
              />
            </label>
          ))}

          <label className="relative block">
            <span className="sr-only">Response</span>
            <select
              aria-label="Response"
              value={filters.response}
              onChange={(e) => setFilters((prev) => ({ ...prev, response: e.target.value }))}
              className="h-10 w-full appearance-none rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 pr-9 text-sm text-[hsl(var(--foreground))] outline-none transition-colors focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/15"
            >
              <option value="">Response</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="partially_accepted">Partially accepted</option>
              <option value="rejected">Rejected</option>
            </select>
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[hsl(var(--muted-foreground))]">
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          </label>

          <label className="relative block">
            <span className="sr-only">Status</span>
            <select
              aria-label="Status"
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
              className="h-10 w-full appearance-none rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 pr-9 text-sm text-[hsl(var(--foreground))] outline-none transition-colors focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/15"
            >
              <option value="">Status</option>
              <option value="submitted">Submitted</option>
              <option value="pending">Pending</option>
              <option value="finalized">Finalized</option>
              <option value="ordered">Ordered</option>
              <option value="awarded">Awarded</option>
              <option value="rejected">Rejected</option>
              <option value="lost">Lost</option>
            </select>
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[hsl(var(--muted-foreground))]">
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          </label>
        </div>

        <div className="mt-3 flex justify-end">
          <ClearFiltersButton
            visible={hasActiveFilters}
            onClick={clearFilters}
            variant="toolbar"
          />
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between text-xs text-[hsl(var(--muted-foreground))]">
        <span>
          {totalItems} {totalItems === 1 ? 'offer' : 'offers'}
          {hasActiveFilters ? ' matching filters' : ''}
          {isFetching ? ' · updating…' : ''}
        </span>
      </div>

      {offers.length === 0 ? (
        <div className="mt-4 flex flex-col items-center justify-center rounded-xl border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--card))] px-6 py-16 text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--muted))]">
            <MdLocalOffer className="h-6 w-6 text-[hsl(var(--muted-foreground))]" />
          </div>
          <h3 className="text-sm font-semibold text-[hsl(var(--foreground))]">
            {hasActiveFilters ? 'No matching offers' : 'No offers yet'}
          </h3>
          <p className="mt-1 max-w-md text-sm text-[hsl(var(--muted-foreground))]">
            {hasActiveFilters
              ? 'Adjust or clear the filters to see more results.'
              : isPurchases
                ? 'Vendor quotations for your purchase RFQs will appear here once vendors respond.'
                : `Send a ${EUROASIANN_OFFER_LABEL} with pricing, quantities, and commercial terms.`}
          </p>
          {!isPurchases && !hasActiveFilters && (
            <button
              type="button"
              onClick={goToNewOffer}
              className="mt-5 inline-flex items-center gap-2 rounded-lg bg-[#0088cc] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0077b3]"
            >
              <MdAdd className="h-5 w-5" />
              Send new offer
            </button>
          )}
        </div>
      ) : (
        <div className="mt-3 mb-8 overflow-hidden rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] border-collapse text-left">
              <thead>
                <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/35">
                  <th className="whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                    Offer #
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                    Title
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                    {isPurchases ? 'Vendor' : 'Recipient'}
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                    RFQ
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                    Amount
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                    Response
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                    Shipping
                  </th>
                  <th className="min-w-[180px] px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                    Workflow
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                    Status
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                    {isPurchases ? 'Received' : 'Sent'}
                  </th>
                  <th className="whitespace-nowrap px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[hsl(var(--border))]">
                {offers.map((offer) => {
                  const rfq =
                    typeof offer.rfqId === 'object' && offer.rfqId ? offer.rfqId : null;
                  const responseLabel = getOfferResponseLabel(offer);
                  const partyLabel = isPurchases
                    ? offerVendorName(offer) || offer.metadata?.recipientEmail || '—'
                    : offer.metadata?.recipientEmail || '—';
                  const openOffer = () => {
                    if (isPurchases) {
                      const linkedRfqId =
                        (typeof offer.rfqId === 'object' && offer.rfqId
                          ? offer.rfqId._id
                          : offer.rfqId) || '';
                      if (linkedRfqId) {
                        navigate(
                          `/dashboard/admin/purchases?purchasesTab=rfqs&rfqId=${linkedRfqId}`
                        );
                        return;
                      }
                    }
                    goToOfferDetail(offer._id);
                  };

                  return (
                    <tr
                      key={offer._id}
                      className="transition-colors hover:bg-[hsl(var(--muted))]/25"
                    >
                      <td className="px-4 py-3.5">
                        <button
                          type="button"
                          onClick={openOffer}
                          className="font-mono text-sm font-semibold text-[#0088cc] hover:underline"
                        >
                          {offer.quotationNumber}
                        </button>
                      </td>
                      <td className="max-w-[220px] px-4 py-3.5">
                        <button
                          type="button"
                          onClick={openOffer}
                          className="block w-full truncate text-left text-sm font-medium text-[hsl(var(--foreground))] hover:text-[#0088cc]"
                          title={offer.title}
                        >
                          {offer.title || '—'}
                        </button>
                      </td>
                      <td className="max-w-[180px] px-4 py-3.5">
                        <p className="truncate text-sm text-[hsl(var(--foreground))]" title={partyLabel}>
                          {partyLabel}
                        </p>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3.5 text-sm text-[hsl(var(--muted-foreground))]">
                        {rfq?.rfqNumber || '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3.5 text-sm font-semibold tabular-nums text-[hsl(var(--foreground))]">
                        {offer.currency} {Number(offer.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-3.5">
                        <span
                          className={cn(
                            'inline-flex rounded-md px-2 py-0.5 text-xs font-semibold',
                            offerResponseBadgeClass(responseLabel)
                          )}
                        >
                          {responseLabel}
                        </span>
                      </td>
                      <td className="max-w-[140px] px-4 py-3.5 text-sm text-[hsl(var(--muted-foreground))]">
                        <span className="line-clamp-2" title={shippingLabel(offer)}>
                          {shippingLabel(offer)}
                        </span>
                      </td>
                      <td className="max-w-[220px] px-4 py-3.5 text-sm text-[hsl(var(--muted-foreground))]">
                        <span className="line-clamp-2" title={responseNotes(offer, pipeline)}>
                          {responseNotes(offer, pipeline)}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span
                          className={cn(
                            'inline-flex rounded-md px-2 py-0.5 text-xs font-semibold',
                            statusBadgeClass(offer.status)
                          )}
                        >
                          {formatStatusLabel(offer.status)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3.5 text-sm text-[hsl(var(--muted-foreground))]">
                        {formatOfferDate(offer.createdAt)}
                      </td>
                      <td className="px-4 py-3.5">
                        <OfferRowActions
                          offer={offer}
                          isPurchases={isPurchases}
                          onOpen={openOffer}
                          onEdit={() => goToOfferEdit(offer._id)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalItems > 0 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              itemsPerPage={limit}
              totalItems={totalItems}
              alwaysShow
            />
          )}
        </div>
      )}
    </div>
  );
}
